import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { UserCircleIcon, CalendarDaysIcon } from "@heroicons/react/24/outline";
import { usePortalAuth } from "../context/PortalAuthContext";
import { portalApi } from "../services/portalApi";
import { PortalShell } from "../components/PortalShell";
import { formatMxLong } from "../utils/dateMx";
import { labelAppointmentModality } from "../utils/consultationModes";

// ── Inline confirm modal ──────────────────────────────────────────────────────
function ConfirmModal({ open, onClose, onConfirm, loading }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-2xl bg-white shadow-2xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-brand-dark">¿Cancelar esta cita?</h2>
        <p className="text-sm text-brand-gray leading-relaxed">
          La sesión quedará liberada y podrás agendar otra fecha si lo necesitas.
        </p>
        <div className="flex gap-3 justify-end pt-1">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-xl text-sm font-medium text-brand-gray hover:bg-brand-beige transition-colors disabled:opacity-50"
          >
            Volver
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-60"
          >
            {loading ? "Cancelando…" : "Sí, cancelar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const isCancelled = status === "cancelled";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${
        isCancelled
          ? "bg-stone-100 text-stone-500"
          : "bg-brand-lavender/15 text-brand-navy"
      }`}
    >
      {isCancelled ? "Cancelada" : "Programada"}
    </span>
  );
}

export default function PortalMyAppointments() {
  const { user, isAuthenticated } = usePortalAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [pendingCancel, setPendingCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = () => {
    setLoading(true);
    portalApi
      .myAppointments()
      .then((r) => setRows(r.appointments || []))
      .catch(() => toast.error("No se pudieron cargar las citas"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  function askCancel(id) {
    setPendingCancel(id);
    setCancelOpen(true);
  }

  async function confirmCancel() {
    if (!pendingCancel) return;
    setCancelling(true);
    try {
      await portalApi.cancelAppointment(pendingCancel);
      toast.success("Cita cancelada");
      setCancelOpen(false);
      setPendingCancel(null);
      load();
    } catch (e) {
      toast.error(e.message || "No se pudo cancelar");
    } finally {
      setCancelling(false);
    }
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role === "admin") return <Navigate to="/admin" replace />;
  if (user?.role === "psychologist") return <Navigate to="/psicologo/citas" replace />;
  if (user?.role !== "client") return <Navigate to="/login" replace />;

  return (
    <PortalShell
      title="Mis citas"
      subtitle="Aquí ves tus sesiones programadas. Puedes cancelar si ya no podrás asistir."
      headerBackgroundImage="/images/agendar-hero.jpg"
    >
      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center gap-3 py-20 text-brand-gray">
          <div className="h-8 w-8 rounded-full border-2 border-brand-lavender/30 border-t-brand-navy animate-spin" />
          <span className="text-sm">Cargando tus citas…</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && rows.length === 0 && (
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-brand-lavender/30 bg-white py-16 px-8 text-center">
          <span className="inline-flex p-4 rounded-full bg-brand-beige text-brand-lavender">
            <CalendarDaysIcon className="h-8 w-8" aria-hidden />
          </span>
          <div>
            <p className="font-semibold text-brand-dark">Aún no tienes citas</p>
            <p className="text-sm text-brand-gray mt-1">
              Cuando agendes una sesión, aparecerá aquí.
            </p>
          </div>
          <Link
            to="/agendar"
            className="mt-2 inline-flex items-center rounded-xl bg-brand-navy px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-brand-navy/20 hover:brightness-110 transition-all"
          >
            Agendar cita
          </Link>
        </div>
      )}

      {/* Count label */}
      {!loading && rows.length > 0 && (
        <p className="text-sm text-brand-gray mb-4">
          {rows.length} cita{rows.length === 1 ? "" : "s"}
        </p>
      )}

      {/* List */}
      <ul className="space-y-4">
        {rows.map((a) => (
          <li key={a.appointmentId}>
            <div className="rounded-2xl border border-brand-lavender/15 bg-white shadow-sm overflow-hidden">
              {/* Status bar top */}
              <div
                className={`h-1 w-full ${
                  a.status === "cancelled" ? "bg-stone-200" : "bg-brand-navy"
                }`}
              />
              <div className="p-5 sm:p-6">
                <div className="flex gap-4 sm:gap-5">
                  {/* Avatar */}
                  <div className="shrink-0">
                    {a.psychologistProfileImageUrl ? (
                      <img
                        src={a.psychologistProfileImageUrl}
                        alt=""
                        className="h-14 w-14 sm:h-16 sm:w-16 rounded-full object-cover border-2 border-white shadow ring-2 ring-brand-lavender/15"
                      />
                    ) : (
                      <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-brand-beige border border-brand-lavender/15 flex items-center justify-center text-brand-gray shadow-sm">
                        <UserCircleIcon className="h-8 w-8 sm:h-9 sm:w-9" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex flex-1 min-w-0 flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-brand-dark text-base leading-tight">
                          {a.psychologistName || "Especialista"}
                        </p>
                        <StatusBadge status={a.status} />
                      </div>
                      <div className="mt-2 space-y-1">
                        <p className="text-sm font-medium text-brand-dark/90">
                          {formatMxLong(a.startUtc)}
                        </p>
                        {a.modality && (
                          <p className="text-sm text-brand-gray">
                            {labelAppointmentModality(a.modality)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Cancel */}
                    {a.status !== "cancelled" && (
                      <div className="shrink-0">
                        <button
                          onClick={() => askCancel(a.appointmentId)}
                          className="rounded-xl border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 hover:border-red-300 transition-colors whitespace-nowrap"
                        >
                          Cancelar cita
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <ConfirmModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={confirmCancel}
        loading={cancelling}
      />
    </PortalShell>
  );
}
